import { Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import NewChatModal from "./components/new-chat-modal.tsx";
import { useMessages } from "./messages.hooks.ts";
import { useNavigate, useParams } from "react-router";
import { useAppSelector } from "@/lib/store.ts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { useState, useMemo } from "react";
import { ChatRoom } from "@/models/chat.model";
import { cn } from "@/lib/utils";

export default function Sidebar() {
  const { chatRooms, newChatOpen, setNewChatOpen } = useMessages();
  const navigate = useNavigate();
  const { roomId: currentRoomId } = useParams<{ roomId?: string }>();
  const [searchQuery, setSearchQuery] = useState("");

  const me = useAppSelector((s) => s.auth.user);
  const users = useAppSelector((s) => s.users);

  // Filter chat rooms based on search query
  const filteredChatRooms = useMemo(() => {
    if (!searchQuery.trim()) return chatRooms;

    const query = searchQuery.toLowerCase().trim();

    return chatRooms.filter((room) => {
      // For direct chats, search by user names
      if (room.type === "direct") {
        const others = room.users.filter((id) => id !== me?.id);
        const userNames = others
          .map((id) => users[id]?.name ?? "")
          .join(" ")
          .toLowerCase();
        return userNames.includes(query);
      }

      // For appointment chats, search by property name and unit number
      if (room.type === "appointment" && room.appointment_data) {
        const appointmentData = room.appointment_data;
        const propertyName = appointmentData.property.name.toLowerCase();
        const unitNumber = appointmentData.unit.unit_number.toLowerCase();
        const serviceName = appointmentData.service?.name?.toLowerCase() ?? "";
        const vendorName = appointmentData.vendor?.display_name?.toLowerCase() ?? "";

        return (
          propertyName.includes(query) ||
          unitNumber.includes(query) ||
          serviceName.includes(query) ||
          vendorName.includes(query)
        );
      }

      if (room.type === "daily_work" && room.daily_work_data) {
        return (room.daily_work_data.property?.name?.toLowerCase() ?? "").includes(query);
      }

      return false;
    });
  }, [chatRooms, searchQuery, me?.id, users]);

  return (
    <>
      <div className="w-80 border-r flex flex-col">
        {/* ───── header ───── */}
        <div className="py-[18px] px-4 border-b space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search conversations…"
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Button variant="outline" size="sm" className="w-full" onClick={() => setNewChatOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Chat
          </Button>
        </div>

        {/* ───── list ───── */}
        <div className="flex-1 overflow-y-auto">
          {filteredChatRooms.length === 0 && searchQuery.trim() ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No conversations found matching "{searchQuery}"
            </div>
          ) : (
            filteredChatRooms.map((room) => {
              /* derive label + avatar */
              let label = "Chat";
              let avatar: string | null | undefined = null;

              if (room.type === "direct") {
                const others = room.users.filter((id) => id !== me?.id);
                label = others.map((id) => users[id]?.name || "…").join(", ");
                avatar = users[others[0]]?.image ?? null;
              } else if (room.type === "appointment" && room.appointment_data) {
                const appointmentData = room.appointment_data;
                label = `${appointmentData.property.name} ${appointmentData.unit.unit_number}`;
              } else if (room.type === "daily_work" && room.daily_work_data) {
                label = `${room.daily_work_data.property?.name ?? "Daily Work"} (DW)`;
              }

              let preview = null;
              if (room.last_message_data && room.last_message_data.length > 0) {
                preview = room.last_message_data[0].is_file
                  ? (room.last_message_data[0].meta_data as any)?.file?.name ?? "Attachment"
                  : room.last_message_data[0].message ?? "";
              }

              const isSelected = currentRoomId === room.id;

              return (
                <button
                  key={room.id}
                  onClick={() => navigate(`/messages/${room.id}`)}
                  className={cn(
                    "w-full text-left p-4 border-b transition-colors",
                    isSelected
                      ? "bg-accent border-l-4 border-l-primary"
                      : "hover:bg-muted"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    {/* avatar */}
                    <Avatar className="h-6 w-6 shrink-0">
                      {avatar && <AvatarImage src={avatar} />}
                      <AvatarFallback>{label.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>

                    {/* room name */}
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-xs font-medium truncate",
                        isSelected && "font-semibold"
                      )}>{label}</p>
                      <p className="text-xs text-muted-foreground truncate">{preview}</p>
                    </div>

                    {/* unread badge */}
                    {(room.unread_count ?? 0) > 0 && (
                      <span className="text-[10px] bg-primary text-white rounded-full px-1">{room.unread_count}</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* modal */}
      <NewChatModal open={newChatOpen} onOpenChange={setNewChatOpen} />
    </>
  );
}
